import { Panel, useReactFlow, useViewport } from '@xyflow/react';
import classnames from 'classnames';
import { type ComponentPropsWithoutRef, forwardRef, memo, useCallback, useMemo } from 'react';

import { useTrackCanvasUploadOptionSelected } from '@air/analytics';
import { TooltipWithShortcut } from '@air/component-tooltip-with-shortcut';
import {
  HandIcon,
  LayoutGridIcon,
  MaximizeIcon,
  MinimizeIcon,
  SquareMousePointerIcon,
  TypeIcon,
  UploadIcon,
} from '@air/design-icons';
import { useChat } from '@air/feature-ag-ui-chat';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { tailwindVariants } from '@air/utils-tailwind';

import { CANVAS_FOOTER_ACTIONS_Z_INDEX } from '../constants';
import { useUploadMediaToCanvas } from '../hooks/useUploadMediaToCanvas';
import { useEditCanvasStore } from '../store';
import { type CanvasToolRailInsertActionId } from './CanvasToolRail/canvasToolRailInsertActions';
import { ToolbarDivider } from './ToolbarDivider';
import { ToolbarThemeToggle } from './ToolbarThemeToggle';
import { ToolbarZoomControls } from './ToolbarZoomControls';
import { useToolbarOffset } from './useToolbarOffset';

const canvasToolbarInsertActionHighlight = tailwindVariants({
  base: 'inline-flex rounded-8',
  defaultVariants: { highlighted: false },
  variants: {
    highlighted: {
      true: 'bg-altitude-3 ring-2 ring-altitude-9 motion-safe:animate-pulse',
    },
  },
});

const canvasToolbarHighlightedAttribute = (highlighted: boolean) => (highlighted ? 'true' : 'false');

type CanvasToolbarInsertActionHighlightProps = ComponentPropsWithoutRef<'span'> & {
  highlighted: boolean;
  testId: string;
};

const CanvasToolbarInsertActionHighlight = forwardRef<HTMLSpanElement, CanvasToolbarInsertActionHighlightProps>(
  ({ children, className, highlighted, testId, ...rest }, ref) => (
    <span
      {...rest}
      className={canvasToolbarInsertActionHighlight({ className, highlighted })}
      data-highlighted={canvasToolbarHighlightedAttribute(highlighted)}
      data-testid={testId}
      ref={ref}
    >
      {children}
    </span>
  ),
);

CanvasToolbarInsertActionHighlight.displayName = 'CanvasToolbarInsertActionHighlight';

const ZOOM_PERCENTAGE_SCALE = 100;

export type ToolbarProps = {
  highlightedInsertActionIds: readonly CanvasToolRailInsertActionId[];
  isFullscreen?: boolean;
  onOpenFullscreen?: () => void;
  onExitFullscreen?: () => void;
  onInsertClick?: () => void;
};

export const Toolbar = memo(
  ({ highlightedInsertActionIds, isFullscreen, onOpenFullscreen, onExitFullscreen, onInsertClick }: ToolbarProps) => {
    const { zoomIn, zoomOut, zoomTo } = useReactFlow();
    const { zoom } = useViewport();
    const activeTool = useEditCanvasStore((state) => state.activeTool);
    const setActiveTool = useEditCanvasStore((state) => state.setActiveTool);
    const canvasId = useEditCanvasStore((state) => state.canvasId);
    const { canConsumeMoreCredits } = useChat();
    const { handleUploadMedia } = useUploadMediaToCanvas();
    const { trackCanvasUploadOptionSelected } = useTrackCanvasUploadOptionSelected();

    const toolbarOffsetStyle = useToolbarOffset();

    const handleUploadMediaClick = useCallback(() => handleUploadMedia(), [handleUploadMedia]);
    const handleUploadFromComputerSelect = useCallback(() => {
      trackCanvasUploadOptionSelected({
        action_taken: 'upload_from_computer',
        location: 'bottom_toolbar',
        ...(canvasId && { canvas_id: canvasId }),
      });
      handleUploadMediaClick();
    }, [trackCanvasUploadOptionSelected, canvasId, handleUploadMediaClick]);
    const handleAddFromBoardSelect = useCallback(() => {
      trackCanvasUploadOptionSelected({
        action_taken: 'add_from_board',
        location: 'bottom_toolbar',
        ...(canvasId && { canvas_id: canvasId }),
      });
      onInsertClick?.();
    }, [trackCanvasUploadOptionSelected, canvasId, onInsertClick]);
    const handleSelectTool = useCallback(() => setActiveTool('select'), [setActiveTool]);
    const handleHandTool = useCallback(() => setActiveTool('hand'), [setActiveTool]);
    const handleTextTool = useCallback(() => setActiveTool('text'), [setActiveTool]);
    const handleZoomOut = useCallback(() => zoomOut(), [zoomOut]);
    const handleZoomIn = useCallback(() => zoomIn(), [zoomIn]);
    const handleZoomTo = useCallback((percentage: number) => zoomTo(percentage / ZOOM_PERCENTAGE_SCALE), [zoomTo]);

    const zoomPercentage = Math.round(zoom * ZOOM_PERCENTAGE_SCALE);
    const isUploadHighlighted = highlightedInsertActionIds.includes('uploadFromComputer');
    const isAddFromBoardHighlighted = highlightedInsertActionIds.includes('addFromBoard');

    const panelStyle = useMemo(
      () => ({ ...toolbarOffsetStyle, zIndex: CANVAS_FOOTER_ACTIONS_Z_INDEX }),
      [toolbarOffsetStyle],
    );

    return (
      <Panel position="bottom-center" style={panelStyle}>
        <div
          className="flex items-center gap-2 rounded-12 border border-gray-4 bg-gray-1/90 p-2 shadow-s backdrop-blur-[18px]"
          data-canvas-toolbar
        >
          {/* Selection tool */}
          <TooltipWithShortcut label="Select" shortcut={['V']}>
            <IconButton
              adsIcon
              appearance="ghost"
              aria-pressed={activeTool === 'select'}
              className={classnames({ 'bg-gray-5': activeTool === 'select' })}
              color="gray"
              icon={SquareMousePointerIcon}
              label="Select"
              onClick={handleSelectTool}
            />
          </TooltipWithShortcut>

          {/* Hand tool */}
          <TooltipWithShortcut label="Hand" shortcut={['H']}>
            <IconButton
              adsIcon
              appearance="ghost"
              aria-pressed={activeTool === 'hand'}
              className={classnames({ 'bg-gray-5': activeTool === 'hand' })}
              color="gray"
              icon={HandIcon}
              label="Hand"
              onClick={handleHandTool}
            />
          </TooltipWithShortcut>

          <ToolbarDivider />

          {onInsertClick ? (
            <>
              <CanvasToolbarInsertActionHighlight
                highlighted={isAddFromBoardHighlighted}
                testId="CANVAS_TOOLBAR_ADD_FROM_BOARD"
              >
                <TooltipWithShortcut label="Select assets to add to Canvas" shortcut={['shift', 'I']}>
                  <IconButton
                    adsIcon
                    appearance="ghost"
                    color="gray"
                    icon={LayoutGridIcon}
                    label="Select assets to add to Canvas"
                    onClick={handleAddFromBoardSelect}
                  />
                </TooltipWithShortcut>
              </CanvasToolbarInsertActionHighlight>
              <CanvasToolbarInsertActionHighlight
                highlighted={isUploadHighlighted}
                testId="CANVAS_TOOLBAR_UPLOAD_FROM_COMPUTER"
              >
                <Tooltip label="Upload from computer">
                  <IconButton
                    adsIcon
                    appearance="ghost"
                    color="gray"
                    disabled={!canConsumeMoreCredits}
                    icon={UploadIcon}
                    label="Upload from computer"
                    onClick={handleUploadFromComputerSelect}
                  />
                </Tooltip>
              </CanvasToolbarInsertActionHighlight>
            </>
          ) : null}

          {!onInsertClick ? (
            <CanvasToolbarInsertActionHighlight highlighted={isUploadHighlighted} testId="CANVAS_TOOLBAR_UPLOAD_IMAGES">
              <Tooltip label="Upload Images">
                <IconButton
                  adsIcon
                  appearance="ghost"
                  color="gray"
                  disabled={!canConsumeMoreCredits}
                  icon={UploadIcon}
                  label="Upload Images"
                  onClick={handleUploadMediaClick}
                />
              </Tooltip>
            </CanvasToolbarInsertActionHighlight>
          ) : null}

          {/* Text tool */}
          <Tooltip label="Text">
            <IconButton
              adsIcon
              appearance="ghost"
              aria-pressed={activeTool === 'text'}
              className={classnames({ 'bg-gray-5': activeTool === 'text' })}
              color="gray"
              disabled={!canConsumeMoreCredits}
              icon={TypeIcon}
              label="Text"
              onClick={handleTextTool}
            />
          </Tooltip>

          <ToolbarDivider />

          {!isFullscreen && onOpenFullscreen && (
            <TooltipWithShortcut label="Open fullscreen" shortcut={['⌘', '.']}>
              <IconButton
                adsIcon
                appearance="ghost"
                color="gray"
                icon={MaximizeIcon}
                label="Open Fullscreen"
                onClick={onOpenFullscreen}
              />
            </TooltipWithShortcut>
          )}
          {isFullscreen && onExitFullscreen && (
            <TooltipWithShortcut label="Exit fullscreen" shortcut={['Esc']}>
              <IconButton
                adsIcon
                appearance="ghost"
                color="gray"
                icon={MinimizeIcon}
                label="Exit Fullscreen"
                onClick={onExitFullscreen}
              />
            </TooltipWithShortcut>
          )}

          <ToolbarThemeToggle />

          {/* Zoom controls */}
          <ToolbarZoomControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomTo={handleZoomTo}
            zoomPercentage={zoomPercentage}
          />
        </div>
      </Panel>
    );
  },
);

Toolbar.displayName = 'Toolbar';
